import { Injectable, inject } from '@angular/core';
import {
  GeneratedTimeBlockComponent,
  ITimeBlockComponentItem,
} from '../types/time-block-component-item-types';
import { CalendarService } from '../../../services/calendar.service';
import { FixInterval, ITimeBlockViewType, TimeBlockContentType } from '../types/time-block-types';
import {
  TimeBlockDayOrWeekFixIntervalType,
  TimeBlockDayOrWeekVariableIntervalType,
  TimeBlockGenericFixIntervalType,
  TimeBlockGenericVariableIntervalType,
  TimeBlockMonthFixIntervalType,
  TimeBlockMonthVariableIntervalType,
} from '../../../../../core/models/timeblock/time-block-view-type.model';
import { clone } from 'lodash-es';
import { TimeBlockModel } from '../../../../../core/models/timeblock/time-block.model';
import { TimeBlockGeometryData } from '../../../../../core/models/timeblock/time-block-geometry-data.model';
import { DateTimeHelper } from '../../../util/date-time-helper';
import { CalendarViews } from '../../../calendar-types';

@Injectable()
export class TimeBlockItemBuilderService {
  private readonly calendarService = inject(CalendarService);

  public buildTimeBlockParts(
    timeBlock: ITimeBlockComponentItem,
    start: Date,
    end: Date,
  ): readonly ITimeBlockComponentItem[] {
    const timeBlockParts = new Array<ITimeBlockComponentItem>();

    if (timeBlock.timeBlockModel.fixInterval === null) {
      end = DateTimeHelper.transformToNearlyMidnightIfNeeded(end);
    }
    const occupiedDays = DateTimeHelper.eachDayOfInterval(start, end);
    occupiedDays.forEach((_, index) => {
      const clonedTimeBlock = timeBlock.clone(this);
      clonedTimeBlock.timeBlockModel.partNumber = index;
      clonedTimeBlock.timeBlockModel.partCount = occupiedDays.length;
      timeBlockParts.push(clonedTimeBlock);
    });

    return timeBlockParts;
  }

  public buildFromExisting(
    timeBlock: ITimeBlockComponentItem | TimeBlockModel,
    timeBlockContentType = TimeBlockContentType.Project,
  ): ITimeBlockComponentItem {
    const tbModel = this.cloneTimeBlockModel(timeBlock);

    let timeBlockViewType: ITimeBlockViewType;
    if (
      this.calendarService.model.calendarViewMode === CalendarViews.DayGrid ||
      this.calendarService.model.calendarViewMode === CalendarViews.WeekGrid
    ) {
      timeBlockViewType =
        tbModel.fixInterval !== null
          ? new TimeBlockDayOrWeekFixIntervalType()
          : new TimeBlockDayOrWeekVariableIntervalType();
    } else if (this.calendarService.model.calendarViewMode === CalendarViews.MonthGrid) {
      timeBlockViewType =
        tbModel.fixInterval !== null
          ? new TimeBlockMonthFixIntervalType()
          : new TimeBlockMonthVariableIntervalType();
    } else {
      // Used for planner events
      timeBlockViewType =
        tbModel.fixInterval !== null
          ? new TimeBlockGenericFixIntervalType()
          : new TimeBlockGenericVariableIntervalType();
    }

    if (!tbModel.timeBlockViewType) {
      tbModel.timeBlockViewType = timeBlockViewType;
    } else {
      Object.assign(timeBlockViewType, tbModel.timeBlockViewType);
      tbModel.timeBlockViewType = timeBlockViewType;
    }

    if (tbModel.componentRef?.instance.timeBlockComponentItem) {
      tbModel.componentRef.instance.timeBlockComponentItem.timeBlockModel = tbModel;
    }

    return new GeneratedTimeBlockComponent(tbModel, timeBlockContentType);
  }

  /**
   * Used to build ghost time blocks
   */
  public buildDefaultTimeBlock(
    start: Date,
    end: Date,
    containerId: number,
    isVariableInterval: boolean,
    timeBlockContentType: TimeBlockContentType,
  ): ITimeBlockComponentItem {
    const timeBlockModel = new TimeBlockModel();
    let timeBlockViewTyp:
      | TimeBlockDayOrWeekFixIntervalType
      | TimeBlockDayOrWeekVariableIntervalType
      | TimeBlockMonthFixIntervalType;
    if (
      this.calendarService.model.calendarViewMode === CalendarViews.DayGrid ||
      this.calendarService.model.calendarViewMode === CalendarViews.WeekGrid
    ) {
      if (isVariableInterval) {
        timeBlockViewTyp = new TimeBlockDayOrWeekVariableIntervalType();
        timeBlockViewTyp.laneIndex = containerId;
      } else {
        timeBlockViewTyp = new TimeBlockDayOrWeekFixIntervalType();
        timeBlockViewTyp.laneIndexStart = containerId;
        timeBlockViewTyp.laneIndexEnd = containerId;
      }
      timeBlockViewTyp.geometryData = new TimeBlockGeometryData();
    } else {
      // Month view
      timeBlockViewTyp = new TimeBlockMonthFixIntervalType();
    }

    timeBlockModel.timeBlockViewType = timeBlockViewTyp;

    const timeBlock = new GeneratedTimeBlockComponent(timeBlockModel, timeBlockContentType);
    timeBlock.timeBlockModel.start = start;
    timeBlock.timeBlockModel.end = end;
    timeBlock.timeBlockModel.fixInterval = isVariableInterval ? null : FixInterval.FullWorkingDay;
    timeBlock.timeBlockModel.userId = this.calendarService.model.calendarProperties.selectedUserId;

    return timeBlock;
  }

  public getTimeBlockStartAndEndDates(timeBlock: ITimeBlockComponentItem): [Date, Date] {
    const offsetStartDate = this.calendarService.model.calendarProperties.offsetStartDate;
    // Use midnight of the next day since time blocks end with 00:00:00.
    const offsetEndDate = this.calendarService.model.calendarProperties.offsetEndDate;

    const start = DateTimeHelper.isBefore(timeBlock.timeBlockModel.start, offsetStartDate)
      ? offsetStartDate
      : timeBlock.timeBlockModel.start;

    const end = DateTimeHelper.isAfter(timeBlock.timeBlockModel.end, offsetEndDate)
      ? offsetEndDate
      : timeBlock.timeBlockModel.end;
    return [start, end];
  }

  private cloneTimeBlockModel(timeBlock: ITimeBlockComponentItem | TimeBlockModel): TimeBlockModel {
    let timeBlockModel: TimeBlockModel;
    if (timeBlock instanceof TimeBlockModel) {
      timeBlockModel = clone(timeBlock);
    } else {
      timeBlockModel = clone(timeBlock.timeBlockModel);
    }

    // Attention: Do not clone the geometry data here since we need a shallow copy of the geometry data.
    return timeBlockModel;
  }
}
